<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import Popper from 'vue3-popper'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DToggleField,
  DModal,
  DLoading,
  Cube01Icon
} from 'front-library'
import {
  ReportAccountingEntry,
  generateAccountingEntry,
  generateAccountingEntryNT,
  ParametersAccountingEntry,
  getAccountingEntryNTRequest,
  getCompaniesAccounting,
  generateRequestAccountingEntries,
  getPayroll,
  getProcessCountry,
  getReportRequest
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsProcesses = ref<string[]>([])
const validGenerateReport = ref(true)
const accountingEntry = ref(false)
const dataAccounting = ref()
const listCreditDebit = ref<any[]>([])
const validSendAccounting = ref(false)
const accountsRequestId = ref('')
const closeModalSuccess = ref(false)
const urlFile = ref(null)
const refresh = ref()
const country = storePayroll.payroll?.country?.iso_code

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  processes: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  })
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<ParametersAccountingEntry>({
  initialValues: { isSummarized: false, hasAuxiliaryFields: false },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: processes } = useField<string[]>(ref('processes'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: isSummarized } = useField<boolean>(ref('isSummarized'))
const { value: hasAuxiliaryFields } = useField<boolean>(
  ref('hasAuxiliaryFields')
)
const { value: haveGroupedAccounts } = useField<boolean>(
  ref('haveGroupedAccounts')
)

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  if (validGenerateReport.value) {
    const path = route.params.code as string
    const decodeCode = atob(path)
    const createReport: ReportAccountingEntry = {
      code: decodeCode,
      parameters: values
    }
    void generateReport(createReport)
  } else {
    const dataGenerate: any = {
      payrolls: payrolls.value,
      processes: processes.value,
      startDate: startDate.value,
      endDate: endDate.value,
      isSummarized: isSummarized.value,
      hasAuxiliaryFields: hasAuxiliaryFields.value
    }
    void generateAccounting(dataGenerate)
  }
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateAccountingEntry(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado de la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Crear solicitud de asiento contable */
const generateAccounting = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.accounting')
  try {
    accountsRequestId.value = await generateAccountingEntryNT(values)
    accountingEntryNTRequest(accountsRequestId.value)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado de la solicitud del asiento contable */
const accountingEntryNTRequest = (id: string) => {
  urlFile.value = null
  try {
    refresh.value = setInterval(async () => {
      const response = await getAccountingEntryNTRequest(id)
      if (response.status === 'VALIDATED') {
        if (response.data.length === 0) {
          accountingEntry.value = false
          typeAlertModal.value = 'error'
          titleModal.value = 'Error'
          descriptionModal.value = t(
            'reports.accountingEntry.notification.noData'
          )
          showModal.value = true
          loading.value = false
          clearInterval(refresh.value)
        } else {
          dataAccounting.value = response.data
          listCreditDebit.value = response.data.map((entry: any) => ({
            isValid: entry.totalDebit === entry.totalCredit
          }))
          accountingEntry.value = true
          loading.value = false
          clearInterval(refresh.value)
        }
      } else if (response.status === 'GENERATED') {
        loading.value = false
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.accountingEntry.notification.success')
        const codes = response.ledger_transactions?.join(', ') || ''
        descriptionModal.value = t(
          'reports.accountingEntry.notification.confirmation',
          { codes }
        )
        showModal.value = true
        closeModalSuccess.value = true
      } else if (response.status === 'ERROR') {
        accountingEntry.value = false
        typeAlertModal.value = 'error'
        titleModal.value = 'Error'
        if (response.errors.length === 1) {
          descriptionModal.value = t(
            'error.messages.' + response.errors[0].code,
            {
              field: validateTranslate(
                'fields.' + response.errors[0].params.field
              ),
              entity: t('fields.' + response.errors[0].params.entity),
              record: response.errors[0].params.record,
              value: response.errors[0].params.value,
              payroll:
                response.errors[0].params.payrollCode &&
                response.errors[0].params.payrollCode != ''
                  ? t('fields.payroll') +
                    ' ' +
                    response.errors[0].params.payrollCode
                  : '',
              message: response.errors[0].params.message
            }
          )
        } else {
          descriptionModal.value = '<div class="text-left">'
          for (let i = 0; i < response.errors.length; i++) {
            const error = t('error.messages.' + response.errors[i].code, {
              field: validateTranslate(
                'fields.' + response.errors[i].params.field
              ),
              entity: t('fields.' + response.errors[i].params.entity),
              record: response.errors[i].params.record,
              payroll:
                response.errors[i].params.payrollCode &&
                response.errors[i].params.payrollCode != ''
                  ? t('fields.payroll') +
                    ' ' +
                    response.errors[i].params.payrollCode
                  : ''
            })
            descriptionModal.value += '- ' + error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
          descriptionModal.value += '</div>'
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    showError(error)
  }
}
function validateTranslate(code: string): any {
  const translated = t(code)
  if (translated === code) {
    return code
  }
  return translated
}

/* animación para mostrar/ocultar detalles */
const dataDetails = (
  id: number,
  movements: number,
  lines: number,
  show: boolean
) => {
  const height = movements + lines
  if (show) {
    document
      .getElementById('details' + id)
      ?.setAttribute(
        'style',
        'max-height: ' +
          (movements * (movements > 1 ? 120 : 140) + lines * 35) +
          'px; transition: max-height ' +
          height / (6 * movements) +
          's ease-out'
      )
    setTimeout(() => {
      document
        .getElementById('btn-show' + id)
        ?.setAttribute('style', 'display: none')
    }, height * (80 / movements) + 40)
  } else {
    document
      .getElementById('details' + id)
      ?.setAttribute(
        'style',
        'max-height: 0px; transition: max-height ' +
          height / (6 * movements) +
          's ease-out'
      )
    document
      .getElementById('btn-show' + id)
      ?.setAttribute('style', 'display: block')
  }
}

/* confirmar el envio de asientos contables a netsuite */
const confirmationSendEntries = () => {
  typeAlertModal.value = 'question'
  titleModal.value =
    dataAccounting.value.length > 1
      ? t('reports.accountingEntry.notification.confirmationMultiple', {
          accounting: dataAccounting.value.length
        })
      : t('reports.accountingEntry.notification.confirmationSingle')
  descriptionModal.value = t('reports.accountingEntry.notification.warning')
  showModal.value = true
}
const sendEntries = async () => {
  showModal.value = false
  loading.value = true
  messageLoading.value = t('loading.send')
  try {
    const response = await generateRequestAccountingEntries(
      dataAccounting.value,
      accountsRequestId.value
    )
    accountingEntryNTRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Cambiar formato de numero */
function formatNumber(val: number) {
  const format = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })
  const formattedNum = format.format(val)
  return formattedNum.replace('$', '')
}

/* Validar campos de fechas (no dejar vacios) */
const validateDate = () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = (valid: boolean) => {
  showModal.value = false
  if (valid) {
    window.location.reload()
  }
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    itemsPayrolls.value = await getPayroll(store.user?.id as string)
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsProcesses.value = await getProcessCountry()
    validSendAccounting.value = await getCompaniesAccounting()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal(closeModalSuccess)"
      @otherMethod="sendEntries">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.accountingEntry.title')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4 mb-3">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-8 gap-4">
                <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls" />
                </div>
                <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.processes')"
                    v-model="processes"
                    :headerTitle="t('reports.fields.processes')"
                    :required="true"
                    :options="itemsProcesses"
                    :error="errors.processes" />
                </div>
                <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="validateDate" />
                </div>
                <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="validateDate" />
                </div>
                <hr class="col-span-8" />
                <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                  <DToggleField
                    v-model="isSummarized"
                    :label="t('reports.fields.summarized')" />
                </div>
                <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                  <DToggleField
                    :label="t('reports.fields.auxiliaryFields')"
                    v-model="hasAuxiliaryFields" />
                </div>
                <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                  <DToggleField
                    :label="t('reports.fields.groupedAccounts')"
                    v-model="haveGroupedAccounts" />
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                v-if="validSendAccounting"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (validGenerateReport = false)"
                v-html="t('form.button.generate')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (validGenerateReport = true)"
                v-html="t('form.button.excel')" />
            </div>
          </template>
        </DCard>
      </div>
      <div v-if="accountingEntry">
        <div
          v-for="(item, x) in dataAccounting"
          class="flex flex-col gap-4"
          :class="[x + 1 === dataAccounting.length ? '' : 'mb-5']">
          <div
            class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-white h-auto min-h-0"
            style="margin-bottom: -5px">
            <div>
              <div class="grid grid-cols-3 p-4">
                <div class="col-span-2">
                  <p class="mb-3 font-semibold">{{ item.payroll.name }}</p>
                  <div class="grid grid-cols-6">
                    <div class="col-span-1">
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.subsidiary')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.billingType')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.invoiceType')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.serviceCountry')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.location')
                        " />
                    </div>
                    <div class="col-span-5">
                      <p class="text-gray-600">
                        {{ item.payroll.subsidiary_name }}
                      </p>
                      <p class="text-gray-600">
                        {{ item.payroll.billing_type_name }}
                      </p>
                      <p class="text-gray-600">
                        {{ item.payroll.invoice_type_name }}
                      </p>
                      <p class="text-gray-600">
                        {{ item.payroll.country_service_name }}
                      </p>
                      <p class="text-gray-600">
                        {{ item.payroll.location_name }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-span-1">
                  <p class="mb-3 text-end font-semibold">
                    {{ dayjs(item.dateIni).format('DD/MM/YYYY') }} -
                    {{ dayjs(item.dateEnd).format('DD/MM/YYYY') }}
                  </p>
                  <div class="grid grid-cols-2 text-center">
                    <div class="col-span-1 border-r border-r-gray-300">
                      <p
                        class="text-gray-600 border-b border-b-gray-300 pb-1"
                        v-html="t('reports.accountingEntry.generate.debit')" />
                      <p
                        class="font-semibold rounded-full text-sm inline-flex px-2 py-1 mt-2"
                        :class="[
                          item.totalDebit === item.totalCredit
                            ? 'text-green-700 bg-green-100'
                            : 'text-red-700 bg-red-100'
                        ]">
                        {{ formatNumber(item.totalDebit) }}
                      </p>
                    </div>
                    <div class="col-span-1">
                      <p
                        class="text-gray-600 border-b border-b-gray-300 pb-1"
                        v-html="t('reports.accountingEntry.generate.credit')" />
                      <p
                        class="font-semibold rounded-full text-sm inline-flex px-2 py-1 mt-2"
                        :class="[
                          item.totalDebit === item.totalCredit
                            ? 'text-green-700 bg-green-100'
                            : 'text-red-700 bg-red-100'
                        ]">
                        {{ formatNumber(item.totalCredit) }}
                      </p>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 mt-5">
                    <div class="col-span-1 text-right">
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.allocationsType')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.deductionsType')
                        " />
                      <p
                        class="text-gray-600"
                        v-html="
                          t('reports.accountingEntry.generate.netPayroll')
                        " />
                    </div>
                    <div class="col-span-1 text-center">
                      <p class="font-semibold text-gray-600">
                        {{ formatNumber(item.totalAssignments) }}
                      </p>
                      <p class="font-semibold text-gray-600">
                        {{ formatNumber(item.totalDeductions) }}
                      </p>
                      <p class="font-semibold text-gray-600">
                        {{ formatNumber(item.totalPayroll) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <hr class="mx-4" />
              <div
                class="overflow-hidden max-h-0"
                :id="'details' + x"
                style="max-height: 0">
                <div v-for="(accounting, i) in item.movements">
                  <div class="p-4">
                    <div class="grid grid-cols-4 bg-gray-100 px-3 py-1 mb-1">
                      <div class="sm:col-span-2 md:col-span-1 text-left">
                        <p
                          class="font-semibold text-gray-700"
                          v-html="
                            t('reports.accountingEntry.generate.entryNumber', [
                              i + 1
                            ])
                          " />
                      </div>
                      <div class="sm:col-span-2 md:col-span-1 text-left">
                        <p class="text-gray-600">
                          {{
                            t(
                              'reports.accountingEntry.generate.allocationsEntryType'
                            )
                          }}
                          <span class="font-semibold text-gray-700">
                            {{ formatNumber(accounting.subTotalAssignments) }}
                          </span>
                        </p>
                      </div>
                      <div class="sm:col-span-2 md:col-span-1 text-right">
                        <p class="text-gray-600">
                          {{
                            t(
                              'reports.accountingEntry.generate.deductionsEntryType'
                            )
                          }}
                          <span class="font-semibold text-gray-700">
                            {{ formatNumber(accounting.subTotalDeductions) }}
                          </span>
                        </p>
                      </div>
                      <div class="sm:col-span-2 md:col-span-1 text-right">
                        <p class="text-gray-600">
                          {{
                            t(
                              'reports.accountingEntry.generate.netPayrollEntryType'
                            )
                          }}
                          <span class="font-semibold text-gray-700">
                            {{ formatNumber(accounting.subTotalPayroll) }}
                          </span>
                        </p>
                      </div>
                    </div>
                    <table class="w-full text-left">
                      <thead
                        class="text-gray-500 bg-gray-100 font-semibold border-b border-b-gray-200">
                        <tr>
                          <th class="px-5">
                            {{
                              t(
                                'reports.accountingEntry.generate.accountingAccount'
                              )
                            }}
                          </th>
                          <th class="px-5">
                            {{
                              t('reports.accountingEntry.generate.costCenter')
                            }}
                          </th>
                          <th class="px-5">
                            {{ t('reports.accountingEntry.generate.concepts') }}
                          </th>
                          <th class="px-5">
                            {{
                              t('reports.accountingEntry.generate.debitAmount')
                            }}
                          </th>
                          <th class="px-5">
                            {{
                              t('reports.accountingEntry.generate.creditAmount')
                            }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="text-gray-600">
                        <tr
                          v-for="line in accounting.lines"
                          class="border-b border-b-gray-200">
                          <td class="px-5">
                            {{ line.accountName }}
                          </td>
                          <td class="px-5">
                            {{ line.costCenterName }}
                          </td>
                          <td class="px-5">
                            <Popper
                              arrow
                              offsetDistance="14"
                              :hover="false"
                              placement="right"
                              class="tooltip">
                              <div class="cursor-pointer">
                                <Cube01Icon
                                  class="w-5 h-5"
                                  style="
                                    margin-top: 3px;
                                    margin-bottom: -3px;
                                  " />
                              </div>
                              <template #content>
                                <div class="text-left">
                                  <div v-for="concept in line.concepts">
                                    • {{ concept }}
                                  </div>
                                </div>
                              </template>
                            </Popper>
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{
                              line.type === 'D'
                                ? formatNumber(line.amount)
                                : formatNumber(0)
                            }}
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{
                              line.type === 'C'
                                ? formatNumber(line.amount)
                                : formatNumber(0)
                            }}
                          </td>
                        </tr>
                      </tbody>
                      <tfoot class="text-gray-600">
                        <tr class="border-b border-b-gray-200">
                          <td colspan="3" class="text-right px-5">
                            {{
                              t('reports.accountingEntry.generate.entryType')
                            }}
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{ formatNumber(accounting.subTotalDebit) }}
                          </td>
                          <td class="px-5 text-gray-700 font-semibold">
                            {{ formatNumber(accounting.subTotalCredit) }}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    <div
                      v-if="item.movements.length - 1 === i"
                      class="flex justify-center">
                      <p
                        class="cursor-pointer text-center font-bold text-blue-600 p-2"
                        @click="
                          dataDetails(
                            x,
                            item.movements.length,
                            item.movements
                              .map((entry: any) => entry.lines.length)
                              .reduce((a: number, b: number) => a + b),
                            false
                          )
                        "
                        v-html="
                          t('reports.accountingEntry.generate.hideDetails')
                        " />
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex justify-center">
                <p
                  class="cursor-pointer text-center font-bold text-blue-600 p-2"
                  :id="'btn-show' + x"
                  @click="
                    dataDetails(
                      x,
                      item.movements.length,
                      item.movements
                        .map((entry: any) => entry.lines.length)
                        .reduce((a: number, b: number) => a + b),
                      true
                    )
                  "
                  v-html="t('reports.accountingEntry.generate.showDetails')" />
              </div>
            </div>
          </div>
        </div>
        <footer
          class="bg-gray-50 py-3 rounded-b-lg px-5 sticky border border-b-2 border-gray-300 bottom-0">
          <div class="flex justify-end gap-2">
            <DBtn
              type="button"
              color="secondary"
              @click="back"
              class="px-5 py-2 font-semibold"
              v-html="t('form.button.back')" />
            <DBtn
              :disabled="listCreditDebit.some(entry => !entry.isValid)"
              :color="
                listCreditDebit.some(entry => !entry.isValid) ? 'disabled' : ''
              "
              class="px-5 py-2 font-semibold"
              @click.prevent="confirmationSendEntries"
              v-html="
                dataAccounting.length > 1
                  ? t('reports.accountingEntry.button.sendMultipleEntries', [
                      dataAccounting.length
                    ])
                  : t('reports.accountingEntry.button.sendSingleEntry')
              " />
          </div>
        </footer>
      </div>
    </form>
  </div>
</template>
